import React, { useMemo } from 'react';
import {
    applyTableFilters,
    applyTableSort
} from '../byor/utils/tableFilterUtils';
import { getLabelStyle } from './widgetLabelHelpers';

// Helper function to calculate pagination slice indices
const getPaginationSlice = (pagination) => {
  if (!pagination) return null;
  const startIndex = pagination.startIndex !== undefined 
    ? pagination.startIndex 
    : pagination.currentPage * pagination.itemsPerPage;
  const endIndex = startIndex + pagination.itemsPerPage;
  return { startIndex, endIndex };
};

export const getDisplayHeader = (config, headerName) => {
  return config?.customColumnHeaders?.[headerName] || headerName;
};

const applyHiddenColumns = (config, columnMap) => {
  const hiddenColumns = config?.hiddenColumns || [];
  if (hiddenColumns.length === 0) return {};
  const overrides = {};
  for (const [colName, showKey] of Object.entries(columnMap)) {
    if (hiddenColumns.includes(colName)) {
      overrides[showKey] = false;
    }
  }
  return overrides;
};

// Data extractor for Local Administrators
export function extractLocalAdministratorsData(data, config = {}) {
  let networkDevices = [];
  if (Array.isArray(data)) {
    networkDevices = data;
  } else if (data?.network_devices && Array.isArray(data.network_devices)) {
    networkDevices = data.network_devices;
  } else if (data?.scanData?.network_devices && Array.isArray(data.scanData.network_devices)) {
    networkDevices = data.scanData.network_devices;
  } else if (data?.data?.network_devices && Array.isArray(data.data.network_devices)) {
    networkDevices = data.data.network_devices;
  } else {
    const keys = Object.keys(data || {});
    const networkKey = keys.find(k => k.toLowerCase().includes('network') && Array.isArray(data[k]));
    if (networkKey) {
      networkDevices = data[networkKey];
    }
  }

  const showDevicesWithNoAdmins = config.showEmptyDevices === true;

  let localAdmins = [];

  networkDevices.forEach(device => {
    const ip = device.ip || '';
    const hostname = device.hostname || device.host || device.name || '';
    const system = device.system || {};
    
    // Fallback: try to find last login from device properties if available globally
    const fallbackLastLogin = system.last_logon || 'Unknown';

    const admins = system.Administrators || [];
    const regularAdmins = system.regular_admins || [];

    // Deduplicate admins by username/account name for this specific device
    const seenAdmins = new Set();
    let hasAdmins = false;

    // Process system.Administrators (typically built-in and local accounts)
    admins.forEach(admin => {
      const username = admin.Username || admin.Account || 'Unknown';
      if (!seenAdmins.has(username)) {
        seenAdmins.add(username);
        hasAdmins = true;
        
        let severity = 'success'; // default
        const accType = (admin['Account Type'] || '').toLowerCase();
        if (accType.includes('built-in administrator')) severity = 'critical';
        else if (accType.includes('domain user') || accType.includes('domain group')) severity = 'critical';
        else severity = 'warning';

        localAdmins.push({
          ip,
          hostname,
          lastLogin: fallbackLastLogin,
          localAdministrator: username,
          accountType: admin['Account Type'] || 'Unknown',
          severity
        });
      }
    });

    // Process system.regular_admins (domain accounts added to local admins)
    regularAdmins.forEach(admin => {
      const username = admin.Account || admin.Username || 'Unknown';
      if (!seenAdmins.has(username)) {
        seenAdmins.add(username);
        hasAdmins = true;
        
        localAdmins.push({
          ip,
          hostname,
          lastLogin: fallbackLastLogin,
          localAdministrator: username,
          accountType: 'Domain/Regular Admin',
          severity: 'warning' // Regular admins found out of baseline
        });
      }
    });

    if (!hasAdmins && showDevicesWithNoAdmins) {
      localAdmins.push({
        ip,
        hostname,
        lastLogin: fallbackLastLogin,
        localAdministrator: 'None',
        accountType: 'N/A',
        severity: 'info'
      });
    }
  });

  return localAdmins;
}

export const LocalAdministratorsTableWidget = ({ data, config = {}, style = {}, pagination }) => {
  // Define base columns for the table
  const baseOverrides = { 'No.': 'showNo', 'IP': 'showIP', 'HOSTNAME': 'showHostname', 'LAST LOGIN': 'showLastLogin', 'LOCAL ADMINISTRATOR': 'showLocalAdministrator' };
  const hiddenOverrides = applyHiddenColumns(config, baseOverrides);
  
  // Default show props
  const defaultShow = { showNo: true, showIP: true, showHostname: true, showLastLogin: true, showLocalAdministrator: true };

  const {
    title = 'Local Administrators',
    ...restConfig
  } = { ...config, ...defaultShow, ...hiddenOverrides };

  // Status visual toggles
  const showStatusIcon = restConfig.showStatusIcon !== false;

  const widgetType = 'local-administrators-table';
  const titleStyle = getLabelStyle(restConfig, 'title', {
    fontSize: '22px',
    fontWeight: '700',
    color: '#000000',
    textAlign: 'left'
  }, widgetType);
  const headerStyleProp = getLabelStyle(restConfig, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType);
  const rowStyleProp = getLabelStyle(restConfig, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType);

  // Deep memoize config
  const serializedConfig = JSON.stringify(restConfig);

  // Extract data using memo
  const allLocalAdminData = useMemo(() => {
    return applyTableSort(
      applyTableFilters(extractLocalAdministratorsData(data, restConfig), restConfig.tableFilters),
      restConfig.tableSort
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, serializedConfig]);

  // Apply pagination if provided
  const displayData = useMemo(() => {
    if (!pagination) return allLocalAdminData;
    const slice = getPaginationSlice(pagination);
    return allLocalAdminData.slice(slice.startIndex, slice.endIndex);
  }, [allLocalAdminData, pagination]);

  // Dynamic grid columns
  const columns = [
    restConfig.showNo ? '0.5fr' : '',
    restConfig.showIP ? '1fr' : '',
    restConfig.showHostname ? '1.5fr' : '',
    restConfig.showLastLogin ? '1fr' : '',
    restConfig.showLocalAdministrator ? '2fr' : ''
  ].filter(Boolean);
  const templateColumns = columns.join(' ') || '1fr';

  // Get font size from style prop, default to 14px
  const tableFontSize = style?.tableFontSize || 14;
  const cellStyle = {
    fontSize: `${tableFontSize}px`,
    padding: '8px 12px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    verticalAlign: 'top',
    textAlign: 'left' // Will be overridden by rowStyleProp alignment if set
  };
  const headerStyle = {
    ...cellStyle,
    ...headerStyleProp,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  };
  const rowStyleMerged = {
    ...cellStyle,
    ...rowStyleProp
  };

  // Helper for status icons
  const renderStatus = (severity) => {
    if (!showStatusIcon) return null;
    
    let icon = '•';
    let color = '#9ca3af'; // Neutral/Info (default)
    
    if (severity === 'critical') {
      icon = '🔴';
      color = '#ef4444'; // Red for critical out of baseline
    } else if (severity === 'warning') {
      icon = '🟡';
      color = '#f59e0b'; // Yellow for standard local admin
    } else if (severity === 'info') {
      icon = '🔵';
      color = '#3b82f6';
    } else if (severity === 'success') {
       icon = '🟢';
       color = '#10b981';
    }
    
    return <span style={{ color, marginRight: '6px' }} title={`Severity: ${severity}`}>{icon}</span>;
  };

  return (
    <div className="open-ports-widget" style={style}>
      {title ? <h3 className="findings-widget-title" style={titleStyle}>{title}</h3> : null}

      <div className="open-ports-table">
          {/* Header Row */}
          <div className="open-ports-header" style={{ gridTemplateColumns: templateColumns, ...headerStyleProp }}>
            {restConfig.showNo ? <div style={headerStyle}>{getDisplayHeader(restConfig, 'No.')}</div> : null}
            {restConfig.showIP ? <div style={headerStyle}>{getDisplayHeader(restConfig, 'IP')}</div> : null}
            {restConfig.showHostname ? <div style={headerStyle}>{getDisplayHeader(restConfig, 'HOSTNAME')}</div> : null}
            {restConfig.showLastLogin ? <div style={headerStyle}>{getDisplayHeader(restConfig, 'LAST LOGIN')}</div> : null}
            {restConfig.showLocalAdministrator ? <div style={headerStyle}>{getDisplayHeader(restConfig, 'LOCAL ADMINISTRATOR')}</div> : null}
          </div>

          {/* Data Rows */}
          <div className="open-ports-body">
            {displayData.length === 0 ? (
              <div className="no-findings-message" style={{ padding: '20px', textAlign: 'center', color: '#666', gridColumn: '1 / -1' }}>
                <span style={{ fontStyle: 'italic', margin: 0 }}>No local administrators found for the selected filters.</span>
              </div>
            ) : (
               <React.Fragment>
                 {displayData.map((item, index) => {
                   const startIndex = pagination && pagination.startIndex !== undefined
                     ? pagination.startIndex
                     : (pagination ? pagination.currentPage * pagination.itemsPerPage : 0);
                   const actualNumber = startIndex + index + 1;

                   const safeHost = String(item?.hostname || 'unk').replace(/[^a-zA-Z0-9]/g, '');
                   const safeAdmin = String(item?.localAdministrator || 'unk').replace(/[^a-zA-Z0-9]/g, '');
                   // accountType could change if properties shift, so we include it for absolute uniqueness if dupes exist
                   const safeType = String(item?.accountType || 'unk').replace(/[^a-zA-Z0-9]/g, '');
                   const uniqueKey = `la-row-${safeHost}-${safeAdmin}-${safeType}`;
                   
                   const hasValidAccountType = item?.accountType && item?.accountType !== 'Unknown' && item?.accountType !== 'N/A';
                   
                   return (
                     <div key={uniqueKey} className="open-ports-row" style={{ gridTemplateColumns: templateColumns, ...rowStyleMerged }}>
                       {restConfig.showNo ? <div className="port-ip" style={rowStyleMerged}><span>{actualNumber}</span></div> : null}
                       
                       {restConfig.showIP ? <div className="port-ip" style={rowStyleMerged}><span>{item?.ip || ''}</span></div> : null}
                       
                       {restConfig.showHostname ? <div className="port-hostname" style={{ ...rowStyleMerged, fontWeight: '500' }}><span>{item?.hostname || ''}</span></div> : null}
                       
                       {restConfig.showLastLogin ? <div className="port-service" style={rowStyleMerged}><span>{item?.lastLogin || ''}</span></div> : null}
                       
                       {restConfig.showLocalAdministrator ? (
                         <div className="port-service" style={{ ...rowStyleMerged, display: 'flex', alignItems: 'center' }}>
                           {renderStatus(item?.severity)}
                           <span>{item?.localAdministrator || ''}</span>
                           {hasValidAccountType ? (
                             <span style={{ fontSize: '0.8em', color: '#6b7280', marginLeft: '6px', fontWeight: 'normal' }}>
                               {`(${item?.accountType})`}
                             </span>
                           ) : null}
                         </div>
                       ) : null}
                     </div>
                   );
                 })}
               </React.Fragment>
            )}
          </div>
      </div>
    </div>
  );
};
